import Skeleton from "@/v2-ui/skeleton"
import Tooltip from "@/v2-ui/tooltip"
import Icon from "@/v2-ui/icon"
import Link from "@/v2-backoffice-client/components/link"
import Dot, { DotText } from "@/v2-backoffice-client/components/dot"
import UptimeHistoryBar from "@/v2-backoffice-client/uptime/UptimeHistoryBar"
import {
  formatRelativeTime,
  formatUptimePercent,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import {
  getInlineStatusLabel,
  getInlineStatusVariant,
  pickInlineStatusKey,
} from "@/v2-backoffice-client/uptime/inline/uptime.inline.utils"
import type { UptimeInlineByKeyQuery } from "@/v2-backoffice-client/uptime/inline/byKey/__types__/uptime.inline.byKey.query"

export type UptimeInlineTarget = NonNullable<
  UptimeInlineByKeyQuery["getUptimeTarget"]
>

/**
 * Days of status history the inline bar shows. Two weeks fits one line of
 * an info item at a cell width that still reads as cells.
 */
const HISTORY_DAYS = 14

export type UptimeInlineStatusProps = {
  target: UptimeInlineTarget | null | undefined
  loading?: boolean
  /**
   * The target's uptime sheet, linked from the tooltip. A string starting
   * with `>>?` pushes onto the view-sheet stack.
   */
  detailHref?: string
}

/**
 * A target's availability as one info-grid value, on one line like every
 * other item: status and 24h uptime, then the last two weeks of checks as
 * a status bar (one cell per day, red where checks failed). The tooltip on
 * the status carries the three uptime windows, the last probe and the link
 * to the full uptime sheet; each bar cell has its own day tooltip.
 */
function UptimeInlineStatus(props: UptimeInlineStatusProps) {
  const { target, loading, detailHref } = props

  if (loading && !target) {
    return <Skeleton className="h-4 w-48" aria-busy="true" />
  }

  if (!target) {
    // The etl only monitors layers with a probeable URL, so an explicit
    // "no monitor" beats an empty value nobody can read.
    return (
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span className="inline-flex">
            <DotText variant="muted">No monitor</DotText>
          </span>
        </Tooltip.Trigger>
        <Tooltip.Content side="bottom">
          No uptime monitor: the probe tracks the layers whose configuration
          carries a URL it can call.
        </Tooltip.Content>
      </Tooltip>
    )
  }

  const statusKey = pickInlineStatusKey(target)
  const statusLabel = getInlineStatusLabel(statusKey)
  const statusVariant = getInlineStatusVariant(statusKey)

  return (
    <span className="flex items-center gap-2">
      <Tooltip>
        <Tooltip.Trigger asChild>
          <span className="inline-flex cursor-help">
            <DotText variant={statusVariant}>
              {statusLabel}
              <span className="tabular-nums">
                {" "}
                · {formatUptimePercent(target.UptimePercent24h)}
              </span>
            </DotText>
          </span>
        </Tooltip.Trigger>
        {/* Tooltips are inverted (bg-foreground / text-background): every
              line inherits that colour; secondary lines only fade it. */}
        <Tooltip.Content side="bottom">
          <span className="flex max-w-xs flex-col gap-1">
            <span className="flex items-center gap-1.5 font-medium">
              <Dot variant={statusVariant} />
              {statusLabel}
              {target.CurrentStatusSince && (
                <> since {formatRelativeTime(target.CurrentStatusSince)}</>
              )}
            </span>
            <span className="tabular-nums">
              Uptime: 24h {formatUptimePercent(target.UptimePercent24h)} · 7d{" "}
              {formatUptimePercent(target.UptimePercent7d)} · 90d{" "}
              {formatUptimePercent(target.UptimePercent90d)}
            </span>
            <span>
              Last checked {formatRelativeTime(target.LastCheckedAt)}
              {target.LastDurationMs != null && (
                <> · {target.LastDurationMs} ms</>
              )}
            </span>
            {target.LastError && statusKey === "down" && (
              <span>
                Last error: {target.LastError}
                {target.LastErrorHttpStatus != null && (
                  <> (HTTP {target.LastErrorHttpStatus})</>
                )}
              </span>
            )}
            {target.IsMuted && (
              <span className="opacity-70">
                Muted{target.MutedByName ? ` by ${target.MutedByName}` : ""}
                {target.MutedReason ? `: ${target.MutedReason}` : ""}
              </span>
            )}
            {detailHref && (
              // The tooltip stays open while the pointer is over it, so the
              // link is reachable; it lives here rather than beside the value
              // to keep the item as narrow as its neighbours.
              <Link
                to={detailHref}
                iconSrc={null}
                size="xs"
                className="uptime-inline-details-link text-background underline"
              >
                Open uptime details
                <Icon src="arrow-right" size={12} className="ml-0.5" />
              </Link>
            )}
          </span>
        </Tooltip.Content>
      </Tooltip>

      <UptimeHistoryBar
        className="uptime-inline-history-bar"
        history={target.DailyHistory}
        days={HISTORY_DAYS}
      />
    </span>
  )
}

export default UptimeInlineStatus
